/**
 * Settings' Programs, or Projects (migration 0081; N4a of the #55 plan,
 * §B.15): the projects a business's revenue and expense lines name, a
 * non-profit's programs.
 *
 * Anyone signed in reads the list; whoever manages the business adds,
 * changes and archives (the API's rule, as for funds). A non-profit's program
 * has a function, which places its expenses on the Statement of Functional
 * Expenses; the function is shown fixed once the program has a posted line,
 * as the server refuses a change to it (PG-2). The default program,
 * Management and General, is where a new expense line starts.
 *
 * Called Program for a non-profit and Project for any other business, until
 * the naming setting is built (§B.15.7).
 */

import { useEffect, useState } from 'react';
import { ApiError, api, type Project, type ProjectFunction, type ProjectInput } from '../api.js';
import { keepsNetAssets, projectWord, useBusiness, useCan } from '../auth/AuthProvider.js';
import { parentsFor, pathOf } from '../lib/tree.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

export const FUNCTION_NAME: Record<ProjectFunction, string> = {
  program_services: 'Program Services',
  management_and_general: 'Management and General',
  fundraising: 'Fundraising',
  membership_development: 'Membership Development',
};

const toInput = (project: Project): ProjectInput => ({
  code: project.code,
  name: project.name,
  parentId: project.parentId,
  function: project.function,
});

function ProjectForm(props: {
  initial: ProjectInput;
  self: string | null;
  projects: Project[];
  nonprofit: boolean;
  word: string;
  functionFixed: string | null;
  submitLabel: string;
  busy: boolean;
  onSubmit: (input: ProjectInput) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<ProjectInput>(props.initial);
  const set = (patch: Partial<ProjectInput>) => setForm((current) => ({ ...current, ...patch }));
  // 0091: any project in use that leaves the tree within three levels, never this one or one under it.
  const parents = parentsFor(props.projects, props.self).filter((project) => project.archivedAt === null);
  return (
    <div className="new-account fund-form" role="group" aria-label={props.submitLabel}>
      <div className="field-row">
        <label className="field field-short">
          <span>Code</span>
          <input
            value={form.code ?? ''}
            maxLength={20}
            onChange={(event) => {
              const code = event.target.value;
              set({ code: code === '' ? null : code });
            }}
          />
        </label>
        <label className="field">
          <span>Name</span>
          <input
            value={form.name}
            maxLength={100}
            onChange={(event) => {
              const name = event.target.value;
              set({ name });
            }}
          />
        </label>
        <label className="field">
          <span>Under</span>
          <select
            value={form.parentId ?? ''}
            onChange={(event) => {
              const parentId = event.target.value;
              set({ parentId: parentId === '' ? null : parentId });
            }}
          >
            <option value="">Nothing (Top Level)</option>
            {parents.map((project) => (
              <option key={project.id} value={project.id}>
                {pathOf(props.projects, project.id)}
              </option>
            ))}
          </select>
        </label>
        {props.nonprofit && (
          <label className="field fund-restriction">
            <span>Function</span>
            <select
              value={form.function ?? ''}
              disabled={props.functionFixed !== null}
              onChange={(event) => {
                const chosen = event.target.value as ProjectFunction | '';
                set({ function: chosen === '' ? null : chosen });
              }}
            >
              <option value="">Choose One…</option>
              {(Object.keys(FUNCTION_NAME) as ProjectFunction[]).map((key) => (
                <option key={key} value={key}>
                  {FUNCTION_NAME[key]}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {props.functionFixed !== null && <p className="muted fund-fixed">{props.functionFixed}</p>}
      <button type="button" disabled={props.busy} onClick={() => props.onSubmit(form)}>
        {props.busy ? 'Saving…' : props.submitLabel}
      </button>
      <button type="button" className="link" onClick={props.onCancel}>
        Cancel
      </button>
    </div>
  );
}

export function ProjectsSection() {
  const can = useCan();
  const manages = can('manage_business');
  const business = useBusiness();
  const nonprofit = keepsNetAssets(business);
  const word = projectWord(business);
  const plural = `${word}s`;
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** 'new' for the add form, a project's id for its edit, null for neither. */
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .listProjects()
      .then((result) => {
        if (live) setProjects(result.projects);
      })
      .catch((caught: unknown) => {
        if (live) setLoadError(caught instanceof ApiError ? caught.message : `Could not read the ${plural.toLowerCase()}.`);
      });
    return () => {
      live = false;
    };
  }, [plural]);

  function replace(project: Project) {
    setProjects((current) => {
      const list = current ?? [];
      return list.some((candidate) => candidate.id === project.id)
        ? list.map((candidate) => (candidate.id === project.id ? project : candidate))
        : [...list, project];
    });
  }

  async function act(run: () => Promise<{ project: Project }>, done: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { project } = await run();
      replace(project);
      setEditing(null);
      setNotice(done);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : `Could not save the ${word.toLowerCase()}.`);
    } finally {
      setBusy(false);
    }
  }


  return (
    <section className="settings-section" aria-labelledby="settings-projects">
      <h2 id="settings-projects">{plural}</h2>
      <p className="muted">
        {nonprofit
          ? 'Every expense is in a program, which places it on the Statement of Functional Expenses.'
          : 'Where money went or what earned it, by client, job or campaign.'}{' '}
        <InfoNote label={`What A ${word} Is`}>
          {nonprofit
            ? "A program is one of the business's activities: tutoring, a food pantry, administration. Its function says whether it is a program service or a supporting activity (management and general, fundraising, membership development), and is fixed once the program has a posted line. A new expense line starts in Management and General. "
            : 'A project goes on revenue and expense lines, and on an asset marked to hold a project’s cost, and never on a bank, receivable or payable line. Projects go three levels deep, such as a client, its campaigns and each campaign’s deliverables. '}
          A {word.toLowerCase()} can be moved on a posted line until the books are closed through its date, since it moves no
          money; closing the books is what protects a filed return. A {word.toLowerCase()} that is no longer used is archived,
          and keeps its lines.
        </InfoNote>
      </p>

      {notice && !error && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {projects === null ? (
        !loadError && <p className="muted">Loading…</p>
      ) : projects.length === 0 ? (
        <p className="muted">No {plural.toLowerCase()} yet.</p>
      ) : (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                {nonprofit && <th>Function</th>}
                <th>Status</th>
                {manages && <th />}
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id}>
                  <td>{pathOf(projects, project.id)}</td>
                  <td>{project.code ?? ''}</td>
                  {nonprofit && <td>{project.function === null ? '' : FUNCTION_NAME[project.function]}</td>}
                  <td>
                    {project.isDefault ? (
                      <span className="tag">Default</span>
                    ) : project.archivedAt !== null ? (
                      <span className="tag">Archived</span>
                    ) : null}
                  </td>
                  {manages && (
                    <td className="line-actions">
                      <button type="button" className="link" aria-label={`Change ${project.name}`} onClick={() => setEditing(project.id)}>
                        Change
                      </button>
                      {!project.isDefault &&
                        (project.archivedAt === null ? (
                          <button
                            type="button"
                            className="link"
                            disabled={busy}
                            aria-label={`Archive ${project.name}`}
                            onClick={() => void act(() => api.archiveProject(project.id), `Archived ${project.name}`)}
                          >
                            Archive
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="link"
                            disabled={busy}
                            aria-label={`Restore ${project.name}`}
                            onClick={() => void act(() => api.restoreProject(project.id), `Restored ${project.name}`)}
                          >
                            Restore
                          </button>
                        ))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {manages &&
        editing !== null &&
        projects !== null &&
        (() => {
          const project = projects.find((candidate) => candidate.id === editing);
          if (editing !== 'new' && project === undefined) return null;
          return (
            <ProjectForm
              key={editing}
              initial={project ? toInput(project) : { code: null, name: '', parentId: null, function: null }}
              self={project?.id ?? null}
              projects={projects}
              nonprofit={nonprofit}
              word={word}
              functionFixed={project?.hasPostedLines && nonprofit ? `${project.name} has posted lines, so its function is fixed.` : null}
              submitLabel={project ? `Save ${word}` : `Add ${word}`}
              busy={busy}
              onCancel={() => setEditing(null)}
              onSubmit={(input) =>
                void act(
                  () => (project ? api.updateProject(project.id, input) : api.createProject(input)),
                  project ? `Saved ${input.name}` : `Added ${input.name}`,
                )
              }
            />
          );
        })()}
      {manages && editing === null && projects !== null && (
        <button type="button" className="link" onClick={() => setEditing('new')}>
          Add a {word}
        </button>
      )}
    </section>
  );
}
